import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  Text,
  FormErrorMessage,
  Flex,
  Badge,
  Divider,
  Collapse,
  Select,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Checkbox,
  FormHelperText,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  IconButton,
  Tooltip,
  Switch,
} from "@chakra-ui/react";
import { LuScrollText } from "react-icons/lu";
import { Header } from "build-ts/metadata";
import { MetadataTypeEnum } from "build-ts/enums";
import { searchPlansheaders } from "services/AnalysisPlanServices";
import { CustomAgentProto, ScenarioProto, CustomAgentProto_ModelConfig } from "build-ts/custom_agent";
import ListPlans from "containers/analysis-plan/ListPlans";
import ScheduledJob from "containers/schedule/ScheduledJob";
import lodash from "lodash";
import { getSlackChannels, getSlackChannelUsers } from "services/CustomAgentServices";
import { SlackChannel, SlackNotification, SlackUser } from "build-ts/slack";
import { FiClock, FiLayers, FiPlus, FiSend, FiSettings } from "react-icons/fi";
import { FaBrain, FaCheck, FaTimes, FaListUl } from "react-icons/fa";
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { modelOptions } from "utils/helperFuncUtils";

interface AgentFormProps {
  initialData?: CustomAgentProto | null;
  onSubmit: (updatedAgent: CustomAgentProto) => void;
  onCancel: () => void;
  isLoading?: boolean;
  setHasFormChanges: (hasChanges: boolean) => void;
  isCreatingNew: boolean;
  isUpdateAgent: boolean;
  setIsUpdateAgent: (isUpdateAgent: boolean) => void;
  isDiscardChanges: boolean;
  setIsDiscardChanges: (isDiscardChanges: boolean) => void;
}

const AgentForm: React.FC<AgentFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  setHasFormChanges,
  isCreatingNew,
  isUpdateAgent,
  setIsUpdateAgent,
  isDiscardChanges,
  setIsDiscardChanges,
}) => {
  const [agentName, setAgentName] = useState<string>("");
  const [agentDescription, setAgentDescription] = useState<string>("");
  const [objective, setObjective] = useState<string>("");
  const [examples, setExamples] = useState<string>("");
  const [keyIssues, setKeyIssues] = useState<string>("");
  const [customInstructions, setCustomInstructions] = useState<string>("");
  const [selectedPlans, setSelectedPlans] = useState<Header[]>([]);
  const [planRuns, setPlanRuns] = useState<Map<string, number>>(new Map());
  const [jobLastRuns, setJobLastRuns] = useState<number>(0);

  const [planHeaders, setPlanHeaders] = useState<Header[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [temperatureError, setTemperatureError] = useState<string>("");

  // State for collapsible sections
  const [isInstructionsExpanded, setIsInstructionsExpanded] = useState<boolean>(false);
  const [isAnalysisPlansExpanded, setIsAnalysisPlansExpanded] = useState<boolean>(false);
  const [isMemoryExpanded, setIsMemoryExpanded] = useState<boolean>(false);
  const [isScheduleExpanded, setIsScheduleExpanded] = useState<boolean>(false);

  // State for slack channels
  const [slackTitle, setSlackTitle] = useState<string>("");
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
  const [selectedSlackChannel, setSelectedSlackChannel] = useState<SlackChannel | null>(null);
  const [channelUsers, setChannelUsers] = useState<SlackUser[]>([]);
  const [selectedSlackUsers, setSelectedSlackUsers] = useState<SlackUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [lastFetchedChannelId, setLastFetchedChannelId] = useState<string | null>(null);
  const [isCommunicationExpanded, setIsCommunicationExpanded] = useState<boolean>(false);

  // State for scenarios
  const [scenarios, setScenarios] = useState<ScenarioProto[]>([]);
  const [isScenariosExpanded, setIsScenariosExpanded] = useState<boolean>(false);
  const [expandedScenarioIndex, setExpandedScenarioIndex] = useState<number | null>(null);

  // State for model config
  const [modelConfig, setModelConfig] = useState<CustomAgentProto_ModelConfig>({
    model_name: "",
    model_type: "",
    temperature: 0.0,
  });
  const [isModelConfigExpanded, setIsModelConfigExpanded] = useState<boolean>(false);

 // Modal state
 const { isOpen: isPlansModalOpen, onOpen: onPlansModalOpen, onClose: onPlansModalClose } = useDisclosure();

  const [originalData, setOriginalData] = useState<{
    agentName: string;
    agentDescription: string;
    objective: string;
    examples: string;
    keyIssues: string;
    customInstructions: string;
    selectedPlans: Header[];
    planRuns: Map<string, number>;
    jobLastRuns: number;
    slackTitle: string;
    selectedSlackChannel: SlackChannel | null;
    selectedSlackUsers: SlackUser[];
    scenarios: ScenarioProto[];
    modelConfig: CustomAgentProto_ModelConfig;
  }>({
    agentName,
    agentDescription,
    objective,
    examples,
    keyIssues,
    customInstructions,
    selectedPlans,
    planRuns,
    jobLastRuns,
    slackTitle,
    selectedSlackChannel,
    selectedSlackUsers,
    scenarios,
    modelConfig,
  });

  const hasValidInitialData = initialData?.header?.id;


  const getCurrentFormData = () => {
    return {
      agentName,
      agentDescription,
      objective,
      examples,
      keyIssues,
      customInstructions,
      selectedPlans,
      planRuns,
      jobLastRuns,
      slackTitle,
      selectedSlackChannel,
      selectedSlackUsers,
      scenarios,
      modelConfig,
    };
  };

  // Check if form has changes or not
  useEffect(()=>{
    const currentFormData = getCurrentFormData();
    const isChanged = !lodash.isEqual(originalData, currentFormData);
    setHasFormChanges(isChanged);
  },[agentName, agentDescription, objective, examples, keyIssues, customInstructions, selectedPlans, planRuns, jobLastRuns, slackTitle, selectedSlackChannel, selectedSlackUsers, scenarios, modelConfig]);

  // Initialize form when initialData changes
  useEffect(() => {
    if (hasValidInitialData) {
      setAgentName(initialData?.header?.name || "");
      setAgentDescription(initialData?.header?.description || "");
      setObjective(initialData?.objective || "");
      setExamples(initialData?.example || "");
      setKeyIssues(initialData?.key_issues || "");
      setCustomInstructions(initialData?.custom_instructions || "");
      setSelectedPlans(initialData?.plan_headers || []);
      // Convert array to Map for planRuns
      const planRunsMap = new Map<string, number>();
      initialData?.plan_runs?.forEach(run => {
        if (run.plan_id) {
          planRunsMap.set(run.plan_id, run.last_runs || 1);
        }
      });
      setPlanRuns(planRunsMap);
      setJobLastRuns(initialData?.job_last_runs || 0);
      setSlackTitle(initialData?.slack_notification?.title || "");
      setSelectedSlackChannel(initialData?.slack_notification?.channel || null);
      setSelectedSlackUsers(initialData?.slack_notification?.users || []);
      setScenarios((initialData?.scenarios || []).map(scenario => ({
        ...scenario,
        disable: scenario.disable ?? false
      })));
      setModelConfig(initialData?.model_config || {
        model_name: "",
        model_type: "",
        temperature: 0.7,
      });
      setOriginalData({
        agentName: initialData?.header?.name || "",
        agentDescription: initialData?.header?.description || "",
        objective: initialData?.objective || "",
        examples: initialData?.example || "",
        keyIssues: initialData?.key_issues || "",
        customInstructions: initialData?.custom_instructions || "",
        selectedPlans: initialData?.plan_headers || [],
        planRuns: planRunsMap,
        jobLastRuns: initialData?.job_last_runs || 0,
        slackTitle: initialData?.slack_notification?.title || "",
        selectedSlackChannel: initialData?.slack_notification?.channel || null,
        selectedSlackUsers: initialData?.slack_notification?.users || [],
        scenarios: (initialData?.scenarios || []).map(scenario => ({
          ...scenario,
          disable: scenario.disable ?? false
        })),
        modelConfig: initialData?.model_config || {
          model_name: "",
          model_type: "",
          temperature: 0.7,
        },
      });
    } else {
      setAgentName("");
      setAgentDescription("");
      setObjective("");
      setExamples("");
      setKeyIssues("");
      setCustomInstructions("");
      setSelectedPlans([]);
      setPlanRuns(new Map());
      setJobLastRuns(0);
      setSlackTitle("");
      setSelectedSlackChannel(null);
      setSelectedSlackUsers([]);
      setScenarios([]);
      setModelConfig({
        model_name: "",
        model_type: "",
        temperature: 0.7,
      });
      setExpandedScenarioIndex(null);
    }
    setErrors({});
  }, [initialData, hasValidInitialData]);

  // Fetch slack channels on component mount
  useEffect(() => {
    const fetchSlackChannels = async () => {
      const slackChannels = await getSlackChannels();
      if(slackChannels) {
        setSlackChannels(slackChannels);
      }
    };
    fetchSlackChannels();
  }, []);

  // Fetch slack channel users when channel is selected
  useEffect(() => {
    const fetchChannelUsers = async () => {
      if (selectedSlackChannel?.id) {
        // Only fetch if we haven't already fetched for this channel
        if (selectedSlackChannel.id === lastFetchedChannelId) {
          return;
        }

        setIsLoadingUsers(true);
        try {
          const users = await getSlackChannelUsers(selectedSlackChannel.id);
          if (users) {
            setChannelUsers(users);
          } else {
            setChannelUsers([]);
          }
          setLastFetchedChannelId(selectedSlackChannel.id);
        } catch (error) {
          console.error("Error fetching channel users:", error);
          setChannelUsers([]);
        } finally {
          setIsLoadingUsers(false);
        }
      } else {
        setChannelUsers([]);
        setLastFetchedChannelId(null);
        // Only clear selectedSlackUsers if we're not initializing from initialData
        if (!hasValidInitialData || !initialData?.slack_notification?.users?.length) {
          setSelectedSlackUsers([]);
        }
        setIsLoadingUsers(false);
      }
    };
    fetchChannelUsers();
  }, [selectedSlackChannel]);

  // Handle Discard Changes
  useEffect(() => {
    if (isDiscardChanges === true) {
      setAgentName(originalData.agentName);
      setAgentDescription(originalData.agentDescription);
      setObjective(originalData.objective);
      setExamples(originalData.examples);
      setKeyIssues(originalData.keyIssues);
      setCustomInstructions(originalData.customInstructions);
      setSelectedPlans(originalData.selectedPlans);
      setPlanRuns(originalData.planRuns);
      setJobLastRuns(originalData.jobLastRuns);
      setSlackTitle(originalData.slackTitle);
      setSelectedSlackChannel(originalData.selectedSlackChannel);
      setSelectedSlackUsers(originalData.selectedSlackUsers);
      setScenarios(originalData.scenarios?.map(scenario => ({
        ...scenario,
        disable: scenario.disable ?? false
      })));
      setModelConfig(originalData.modelConfig);
      setExpandedScenarioIndex(null);
      setErrors({});
      setIsDiscardChanges(false);
    }
  }, [isDiscardChanges]);

  // handle update agent
  useEffect(() => {
    if (isUpdateAgent === true) {
      handleSubmit();
      setIsUpdateAgent(false);
    }
  }, [isUpdateAgent]);

  // Fetch plan headers on component mount
  useEffect(() => {
    const fetchPlans = async () => {
        const plans = await searchPlansheaders(
          -1 /* pageSize */,
          1 /* pageNumber */,
          "LAST_MODIFIED" /* sortByField */,
          false /* sortAscending */,
          "" /* searchString */,
          [] /* metricIds */,
          false /* includeScheduleActivity */,
          false /* activeSchedule */
        );
        if (plans) {
          setPlanHeaders(plans.headers);
        }
    };
    fetchPlans();
  }, []);

  // Sync planRuns with selectedPlans changes.
  useEffect(() => {
    // Create new Map for selected plans only, preserving existing run counts
    const newPlanRunsMap = new Map<string, number>();
    selectedPlans?.forEach(plan => {
      if (plan.id) {
        const existingRuns = planRuns?.get(plan.id) || 1; // preserve existing or default to 1
        newPlanRunsMap.set(plan.id, existingRuns);
      }
    });
    setPlanRuns(newPlanRunsMap);
  }, [selectedPlans]);

  // Handle plan runs change - ONLY for currently selected plans
  const handlePlanRunsChange = (planId: string, value: number) => {
    // Ensure value is a valid number, default to 1 if invalid
    const validValue = isNaN(value) || value < 1 ? 1 : value;
    setPlanRuns(prev => {
      const newMap = new Map(prev);
      newMap.set(planId, validValue);
      return newMap;
    });
  };

  // Form validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!agentName.trim()) newErrors.name = "Agent name is required";
    if (!objective.trim()) newErrors.objective = "Agent objective is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Toggle sections expand/collapse
  const toggleAnalysisPlansExpansion = () => {
    setIsAnalysisPlansExpanded(!isAnalysisPlansExpanded);
    setIsMemoryExpanded(false);
    setIsScheduleExpanded(false);
    setIsCommunicationExpanded(false);
    setIsInstructionsExpanded(false);
    setIsScenariosExpanded(false);
    setIsModelConfigExpanded(false);
  };

  const toggleMemoryExpansion = () => {
    setIsMemoryExpanded(!isMemoryExpanded);
    setIsAnalysisPlansExpanded(false);
    setIsScheduleExpanded(false);
    setIsCommunicationExpanded(false);
    setIsInstructionsExpanded(false);
    setIsScenariosExpanded(false);
    setIsModelConfigExpanded(false);
  };
  const toggleScheduleExpansion = () => {
    setIsScheduleExpanded(!isScheduleExpanded);
    setIsMemoryExpanded(false);
    setIsAnalysisPlansExpanded(false);
    setIsCommunicationExpanded(false);
    setIsInstructionsExpanded(false);
    setIsScenariosExpanded(false);
    setIsModelConfigExpanded(false);
  };

  const toggleCommunicationExpansion = () => {
    setIsCommunicationExpanded(!isCommunicationExpanded);
    setIsMemoryExpanded(false);
    setIsAnalysisPlansExpanded(false);
    setIsScheduleExpanded(false);
    setIsInstructionsExpanded(false);
    setIsScenariosExpanded(false);
    setIsModelConfigExpanded(false);
  };

  const toggleInstructionsExpansion = () => {
    setIsInstructionsExpanded(!isInstructionsExpanded);
    setIsMemoryExpanded(false);
    setIsAnalysisPlansExpanded(false);
    setIsScheduleExpanded(false);
    setIsCommunicationExpanded(false);
    setIsScenariosExpanded(false);
    setIsModelConfigExpanded(false);
  };

  const toggleScenariosExpansion = () => {
    setIsScenariosExpanded(!isScenariosExpanded);
    setIsMemoryExpanded(false);
    setIsAnalysisPlansExpanded(false);
    setIsScheduleExpanded(false);
    setIsCommunicationExpanded(false);
    setIsInstructionsExpanded(false);
    setIsModelConfigExpanded(false);
  };

  const toggleModelConfigExpansion = () => {
    setIsModelConfigExpanded(!isModelConfigExpanded);
    setIsMemoryExpanded(false);
    setIsAnalysisPlansExpanded(false);
    setIsScheduleExpanded(false);
    setIsCommunicationExpanded(false);
    setIsInstructionsExpanded(false);
    setIsScenariosExpanded(false);
  };

  // Toggle individual scenario expansion
  const toggleScenarioExpansion = (index: number) => {
    setExpandedScenarioIndex(expandedScenarioIndex === index ? null : index);
  };

  // Handle Slack channel selection
  const handleSlackChannelChange = (channelId: string) => {
    if (channelId === "") {
      setSelectedSlackChannel(null);
    } else {
      const channel = slackChannels.find(c => c.id === channelId);
      setSelectedSlackChannel(channel || null);
    }
    setSelectedSlackUsers([]); // Reset selected users when channel changes
  };

  // Remove selected slack channel user
  const removeSlackUser = (userId: string) => {
    setSelectedSlackUsers(prev => prev.filter(u => u.id !== userId));
  };

  // Scenario management functions
  const addScenario = () => {
    // Generate next available integer ID based on existing IDs
    const existingIds = scenarios?.map(s => s.id || 0);
    const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
    const newScenario = ScenarioProto.create({
      id: nextId,
      issue: "",
      trigger: "",
      investigation: "",
      reporting: "",
      disable: false,
    });
    setScenarios(prev => {
      const newScenarios = [...prev, newScenario];
      // Automatically expand the newly added scenario
      setExpandedScenarioIndex(newScenarios.length - 1);
      return newScenarios;
    });
  };

  const updateScenario = (index: number, field: keyof ScenarioProto, value: string | number | boolean) => {
    setScenarios(prev => prev.map((scenario, i) =>
      i === index ? { ...scenario, [field]: value } : scenario
    ));
  };

  const removeScenario = (index: number) => {
    setScenarios(prev => prev.filter((_, i) => i !== index));
    // Reset expanded scenario index if we removed the expanded one
    if (expandedScenarioIndex === index) {
      setExpandedScenarioIndex(null);
    } else if (expandedScenarioIndex !== null && expandedScenarioIndex > index) {
      // Adjust index if we removed a scenario before the expanded one
      setExpandedScenarioIndex(expandedScenarioIndex - 1);
    }
  };

  // Model config update function
  const updateModelConfig = (field: keyof CustomAgentProto_ModelConfig, value: string | number) => {
    setModelConfig(prev => ({ ...prev, [field]: value }));
  };

  // Temperature validation function
  const validateTemperature = (value: string) => {
    if (value === "") {
      setTemperatureError("");
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setTemperatureError("Please enter a valid number");
      return;
    }

    if (numValue < 0) {
      setTemperatureError("Temperature cannot be negative");
      return;
    }

    if (numValue > 2) {
      setTemperatureError("Temperature cannot be greater than 2.0");
      return;
    }

    setTemperatureError("");
  };

  // Function to get model type based on selected model name
  const getModelTypeForModel = (modelName: string): string => {
    for (const [type, models] of Object.entries(modelOptions)) {
      if (models.some(m => m.model_name === modelName)) {
        return type;
      }
    }
    return "";
  };

  // Handle model name change and auto-set model type
  const handleModelNameChange = (selectedModel: string) => {
    const modelType = getModelTypeForModel(selectedModel);
    setModelConfig(prev => ({
      ...prev,
      model_name: selectedModel,
      model_type: modelType
    }));
  };



  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    const agentHeader = Header.create({
      id: hasValidInitialData ? initialData?.header?.id : undefined,
      name: agentName.trim(),
      description: agentDescription.trim(),
      type: MetadataTypeEnum.CUSTOM_AGENT,
    });

    // Convert Map back to array format for protobuf
    const planRunsArray = Array.from(planRuns.entries()).map(([plan_id, last_runs]) => ({
      plan_id,
      last_runs,
    }));

    const customAgent = CustomAgentProto.create({
      header: agentHeader,
      plan_headers: selectedPlans,
      plan_runs: planRunsArray,
      job_last_runs: jobLastRuns,
      objective: objective,
      example: examples,
      key_issues: keyIssues,
      slack_notification: selectedSlackChannel ? SlackNotification.create({
        title: slackTitle,
        channel: selectedSlackChannel,
        users: selectedSlackUsers,
      }) : undefined,
      custom_instructions: customInstructions,
      scenarios: scenarios,
      model_config: modelConfig.model_name || modelConfig.model_type ? modelConfig : undefined,
    });

    onSubmit(customAgent);
  };

  return (
    <Box
      mx="auto"
      height="100%"
      overflowY="auto"
      sx={{
        "&::-webkit-scrollbar": {
          display: "none"
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none"
      }}
    >
      <Box>
        {/* Basic Information */}
        <VStack spacing={3} align="stretch" p={2}>
          <HStack spacing={4}>
            <FormControl isInvalid={!!errors.name} flex="1">
            <HStack spacing={3} align="center">
              <FormLabel fontSize="sm" mb={0} fontWeight="600" minW="100px"> Name</FormLabel>
              <Input
                size="sm"
                focusBorderColor="purple.700"
                value={agentName}
                onChange={(e) => {
                  setAgentName(e.target.value);
                  if (errors.name) setErrors(prev => ({ ...prev, name: "" }));
                }}
                placeholder="Enter agent name"
                _focus={{
                  borderColor: "purple.700",
                  boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                }}
                fontWeight="500"
              />
              </HStack>
              <FormErrorMessage fontSize="xs">{errors.name}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors.description}>
          <HStack spacing={3} align="center">
          <FormLabel fontSize="sm" mb={0} fontWeight="600" minW="100px">Description</FormLabel>
            <Input
              size="sm"
              value={agentDescription}
              onChange={(e) => {
                setAgentDescription(e.target.value);
                if (errors.description) setErrors(prev => ({ ...prev, description: "" }));
              }}
              placeholder="Enter agent description"
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
              }}
              fontWeight="500"
            />
            </HStack>
            <FormErrorMessage fontSize="xs">{errors.description}</FormErrorMessage>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1}/>

        {/* Instructions Section */}
        <VStack spacing={3} align="stretch">
          <FormControl>

            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleInstructionsExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
              //bg={isInstructionsExpanded ? "gray.50" : "white"}
            >
              <Icon as={LuScrollText} boxSize={5} color="fg.default" />
              <FormLabel fontSize="md" mb={0} cursor="pointer" fontWeight="700" color="fg.default">
                Instructions
              </FormLabel>
              {!isInstructionsExpanded && (
                  <Text fontSize="sm" color="gray.600" mb={0} align="left" ml={1} mt={1}>
                  Configure the agent's objective, instructions, and examples
                  </Text>
              )}
            </HStack>

            <Collapse in={isInstructionsExpanded} animateOpacity>
              <VStack spacing={3} align="stretch" p={2}>
                <HStack spacing={3} align="center">
                <FormControl isInvalid={!!errors.objective}>
                  <FormLabel fontSize="sm" mb={1} fontWeight="700">Objective</FormLabel>
                  <Textarea
                    size="sm"
                    value={objective}
                    onChange={(e) => {
                      setObjective(e.target.value);
                      if (errors.objective) setErrors(prev => ({ ...prev, objective: "" }));
                    }}
                    placeholder="Enter the agent's objective and purpose"
                    rows={7}
                    _focus={{
                      borderColor: "purple.700",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                    }}
                    fontWeight="500"
                    resize="none"
                  />
                  <FormErrorMessage fontSize="xs">{errors.objective}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.examples}>
                  <FormLabel fontSize="sm" mb={1} fontWeight="700">General Instructions</FormLabel>
                  <Textarea
                    size="sm"
                    // value={examples}
                    // onChange={(e) => {
                    //   setExamples(e.target.value);
                    //   if (errors.examples) setErrors(prev => ({ ...prev, examples: "" }));
                    // }}
                    placeholder="General instructions for the agent..."
                    rows={7}
                    _focus={{
                      borderColor: "purple.700",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                    }}
                    fontWeight="500"
                    resize="none"
                  />
                  <FormErrorMessage fontSize="xs">{errors.examples}</FormErrorMessage>
                </FormControl>
                </HStack>

                <HStack spacing={3} align="center">
                <FormControl isInvalid={!!errors.keyIssues}>
                  <FormLabel fontSize="sm" mb={1} fontWeight="700">Key Issues</FormLabel>
                  <Textarea
                    size="sm"
                    value={keyIssues}
                    onChange={(e) => {
                      setKeyIssues(e.target.value);
                      if (errors.keyIssues) setErrors(prev => ({ ...prev, keyIssues: "" }));
                    }}
                    placeholder="Enter key issues that the agent should highlight in the output..."
                    rows={7}
                    _focus={{
                      borderColor: "purple.700",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                    }}
                    fontWeight="500"
                    resize="none"
                  />
                  <FormErrorMessage fontSize="xs">{errors.keyIssues}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.examples}>
                  <FormLabel fontSize="sm" mb={1} fontWeight="700">Examples (few shot)</FormLabel>
                  <Textarea
                    size="sm"
                    value={examples}
                    onChange={(e) => {
                      setExamples(e.target.value);
                      if (errors.examples) setErrors(prev => ({ ...prev, examples: "" }));
                    }}
                    placeholder="Enter few shot examples for the agent..."
                    rows={7}
                    _focus={{
                      borderColor: "purple.700",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                    }}
                    fontWeight="500"
                    resize="none"
                  />
                  <FormErrorMessage fontSize="xs">{errors.examples}</FormErrorMessage>
                </FormControl>
                </HStack>
              </VStack>
            </Collapse>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1}/>

        {/* Plans Configuration */}
        <VStack spacing={3} align="stretch">
          <FormControl isInvalid={!!errors.selectedPlans}>
            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleAnalysisPlansExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
            >
              <Icon as={FiLayers} boxSize={5} color="fg.default" />
              <FormLabel fontSize="md" mb={0} cursor="pointer" fontWeight="700" color="fg.default">
                Plans {selectedPlans.length > 0 && (
                  <Badge colorScheme="purple" ml={2}>{selectedPlans.length}</Badge>
                )}
              </FormLabel>
              {!isAnalysisPlansExpanded && (
                <Text fontSize="sm" color="gray.600" mb={0} align="left" ml={1} mt={1}>
                  Select the analysis plans that the agent can access and execute
                </Text>
              )}
            </HStack>

            <Collapse in={isAnalysisPlansExpanded} animateOpacity>
              <VStack spacing={3} align="stretch">
              {/* Show selected plan names when collapsed */}
             {isAnalysisPlansExpanded && selectedPlans.length > 0 && (
               <Box p={2}>
               <Text fontSize="sm" color="fg.default" fontWeight="600" textAlign="left" ml={1}>
                 <Text as="span" fontWeight="600" color="fg.default">
                   {agentName} {" "}
                 </Text>
                 Agent has access to the following plans:
               </Text>
               <VStack spacing={1} align="flex-start">
                  {selectedPlans.map((plan, index) => (
                    <HStack spacing={2} align="center">
                      <Text key={plan.id} fontSize="sm" color="gray.700" textAlign="left" mb={0}>
                        <Text as="span" color="fg.default" ml={1} mr={2} mb={0}>
                          {index + 1}.
                        </Text>
                        <Text as="span" fontWeight="bold" color="fg.default" mb={0}>
                          {plan.name}
                        </Text>
                      </Text>
                      <IconButton
                        aria-label="Open Plan"
                        icon={<ExternalLinkIcon />}
                        size="xs"
                        color="purple"
                        variant="ghost"
                        colorScheme="gray"
                        onClick={()=>{
                          window.open(`/plan/${plan.id}`, "_blank");
                        }}
                      />
                    </HStack>
                  ))}
               </VStack>
             </Box>
            )}
            <HStack justify="space-between" align="center" px={2}>
              <Button
                size="sm"
                leftIcon={<Icon as={FiPlus} />}
                colorScheme="purple"
                variant="solid"
                onClick={(e) => {
                  e.stopPropagation();
                  onPlansModalOpen();
                }}
                minW="auto"
                flexShrink={0}
                mb={2}
                ml={1}
                bg="purple.700"
                fontWeight="700"
              >
                Add Plans
              </Button>
              </HStack>
              {/* Plans Selection Modal */}
              <Modal isOpen={isPlansModalOpen} onClose={onPlansModalClose} size="2xl">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>Select Analysis Plans</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Box maxHeight="400px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md">
                      <ListPlans
                        planHeaders={planHeaders}
                        selectedPlanHeaders={selectedPlans}
                        setSelectedPlanHeaders={setSelectedPlans}
                        isMultiSelect={true}
                      />
                    </Box>

                    {/* Selected Plans Preview in Modal */}
                    {selectedPlans.length > 0 && (
                      <Box mt={4}>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                          Selected Plans ({selectedPlans.length}):
                        </Text>
                        <HStack spacing={2} flexWrap="wrap">
                          {selectedPlans.map(plan => (
                            <Badge key={plan.id} colorScheme="purple" fontSize="xs">
                              {plan.name}
                            </Badge>
                          ))}
                        </HStack>
                      </Box>
                    )}
                  </ModalBody>
                  <ModalFooter>
                    <Button colorScheme="purple" onClick={onPlansModalClose}>
                      Done
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>

              </VStack>
            </Collapse>
            <FormErrorMessage fontSize="xs">{errors.selectedPlans}</FormErrorMessage>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1}/>

        {/* Memory Configuration */}
        <VStack spacing={3} align="stretch">
          <FormControl>
            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleMemoryExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
            >
             <Icon as={FaBrain} boxSize={5} color="fg.default" />
             <FormLabel fontSize="md" mb={0} cursor="pointer" fontWeight="700" color="fg.default">
                Memory
              </FormLabel>
              {!isMemoryExpanded && (
                <Text fontSize="sm" color="gray.600" mb={0} align="left" ml={1} mt={1}>
                  Set how many past runs the agent remembers for context
                </Text>
              )}
            </HStack>

            <Collapse in={isMemoryExpanded} animateOpacity>
              <VStack spacing={3} align="stretch" p={2}>
                <FormControl isInvalid={!!errors.customInstructions}>
                  <FormLabel fontSize="sm" mb={1} fontWeight="600">Custom Instructions</FormLabel>
                  <Textarea
                    size="sm"
                    value={customInstructions}
                    onChange={(e) => {
                      setCustomInstructions(e.target.value);
                      if (errors.customInstructions) setErrors(prev => ({ ...prev, customInstructions: "" }));
                    }}
                    placeholder="Enter additional specific context or instructions for the agent..."
                    rows={7}
                    _focus={{
                      borderColor: "purple.700",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                    }}
                    fontWeight="500"
                  />
                  <FormErrorMessage fontSize="xs">{errors.customInstructions}</FormErrorMessage>
                  <FormHelperText fontSize="xs" color="fg.default" textAlign="left">
                    Note: The agent will have access to change this field.
                  </FormHelperText>
                </FormControl>
                  <Badge fontSize="sm" color="fg.default" fontWeight="700" textAlign="left" mb={0} bg="purple.50" px={2} m={0}>Set Memory Instances:</Badge>
                <FormControl>
                  <HStack spacing={3} align="center" justify="space-between">
                  <FormLabel fontSize="sm" mb={0} fontWeight="700" ml={2}>Self Output</FormLabel>
                  <HStack spacing={2}>
                  <Text fontSize="sm" color="fg.default" mb={0}>Past</Text>
                  <NumberInput
                    size="sm"
                    value={jobLastRuns}
                    min={0}
                    max={100}
                    onChange={(_, value) => setJobLastRuns(value)}
                    width="80px"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm" color="fg.default" mb={0}>runs</Text>
                  </HStack>
                  </HStack>
                </FormControl>
              </VStack>
            {/* Selected Plans Configuration */}
            {selectedPlans.length > 0 && (
              <VStack spacing={0} align="stretch">
                {selectedPlans.map(plan => {
                  const planRunCount = planRuns.get(plan.id!) || 1;
                  return (
                    <HStack
                      key={plan.id}
                      spacing={4}
                      align="center"
                      p={2}
                      borderRadius="md"
                      fontSize="sm"
                    >
                      <HStack spacing={0} align="center" flex="1">
                        <Text fontSize="sm" fontWeight="700" color="fg.default" mb={0} ml={2}>
                          {plan.name}
                        </Text>
                      </HStack>

                      <HStack spacing={2} align="center">
                        <Text fontSize="sm" color="fg.default" mb={0}>
                          Past
                        </Text>
                        <NumberInput
                          size="sm"
                          value={planRunCount}
                          min={1}
                          max={100}
                          onChange={(valueAsString, valueAsNumber) => {
                            handlePlanRunsChange(plan.id!, valueAsNumber);
                          }}
                          width="80px"
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        <Text fontSize="sm" color="fg.default" mb={0}>
                          runs
                        </Text>
                      </HStack>
                    </HStack>
                  );
                })}
              </VStack>
            )}
            </Collapse>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1}/>

        {/* Communication Section */}
        <VStack spacing={3} align="stretch">
          <FormControl>
            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleCommunicationExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
            >
              <Icon as={FiSend} boxSize={5} color="fg.default" />
              <FormLabel fontSize="md" mb={0} cursor="pointer" fontWeight="700" color="fg.default">
                Communication {(selectedSlackChannel || selectedSlackUsers.length > 0) && (
                  <Badge colorScheme="purple" ml={2} textTransform="none" fontWeight="700">
                    {selectedSlackChannel ? 1 : 0} channel, {selectedSlackUsers.length} users
                  </Badge>
                )}
              </FormLabel>
              {!isCommunicationExpanded && (
                <Text fontSize="sm" color="gray.600" mb={0} align="left" ml={1} mt={1}>
                  Configure where the agent delivers insights and notifications
                </Text>
              )}
            </HStack>

            <Collapse in={isCommunicationExpanded} animateOpacity>
              <VStack spacing={3} align="stretch" p={3}>
                <FormControl>
                  {/* Title */}
                  <FormLabel fontSize="sm" mb={1} fontWeight="600">Title</FormLabel>
                  <Input
                    size="sm"
                    value={slackTitle}
                    onChange={(e) => {
                      setSlackTitle(e.target.value);
                    }}
                    placeholder="Enter title"
                  />
                  {/* Channel Selection */}
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1} mt={2}fontWeight="600">Select Channel</FormLabel>
                    <Select
                      size="sm"
                      value={selectedSlackChannel?.id || ""}
                      onChange={(e) => handleSlackChannelChange(e.target.value)}
                      placeholder="Choose a channel"
                    >
                      {slackChannels.map(channel => (
                        <option key={channel.id} value={channel.id}>
                          {channel.name}
                        </option>
                      ))}
                    </Select>
                    {selectedSlackChannel && (
                      <Flex mt={2}>
                        <Badge colorScheme="blue" fontSize="xs">
                          {selectedSlackChannel.name}
                        </Badge>
                      </Flex>
                    )}
                  </FormControl>
                  {/* User Selection */}
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1} mt={2} fontWeight="600">Select Users</FormLabel>
                    <Popover placement="bottom-start" strategy="fixed">
                      <PopoverTrigger>
                        <Button
                          size="sm"
                          variant="outline"
                          isDisabled={!selectedSlackChannel || isLoadingUsers}
                          isLoading={isLoadingUsers}
                          loadingText="Loading users..."
                          bg={(!selectedSlackChannel || isLoadingUsers) ? "gray.100" : "white"}
                          textAlign="left"
                          fontWeight="normal"
                          width="100%"
                          justifyContent="flex-start"
                          _hover={(!selectedSlackChannel || isLoadingUsers) ? {} : { bg: "gray.50" }}
                        >
                          {isLoadingUsers
                            ? "Loading users..."
                            : selectedSlackUsers.length === 0
                            ? (selectedSlackChannel ? "Choose users" : "Select a channel first")
                            : selectedSlackUsers.length === 1
                            ? selectedSlackUsers[0].display_name || selectedSlackUsers[0].name
                            : `${selectedSlackUsers.length} users selected`
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent width="300px" zIndex="popover">
                        <PopoverBody maxHeight="200px" overflowY="auto" p={3}>
                          <VStack spacing={2} align="stretch">
                            {isLoadingUsers ? (
                              <Text fontSize="sm" color="gray.500" textAlign="center">
                                Loading users...
                              </Text>
                            ) : channelUsers.length === 0 ? (
                              <Text fontSize="sm" color="gray.500" textAlign="center">
                                No users available
                              </Text>
                            ) : (
                              channelUsers.map(user => (
                                <HStack key={user.id} spacing={3} w="100%" align="center">
                                  <Checkbox
                                    isChecked={selectedSlackUsers.some(u => u.id === user.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        if (!selectedSlackUsers.some(u => u.id === user.id)) {
                                          setSelectedSlackUsers(prev => [...prev, user]);
                                        }
                                      } else {
                                        setSelectedSlackUsers(prev => prev.filter(u => u.id !== user.id));
                                      }
                                    }}
                                    size="sm"
                                    colorScheme="blue"
                                  />
                                  <Text fontSize="sm" color="gray.700" ml={1} mt={1}>
                                    {user.display_name || user.name}
                                  </Text>
                                </HStack>
                              ))
                            )}
                          </VStack>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                    {selectedSlackUsers.length > 0 && (
                      <Flex mt={2} flexWrap="wrap" gap={2}>
                        {selectedSlackUsers.map(user => (
                          <Badge
                            key={user.id}
                            colorScheme="purple"
                            fontSize="xs"
                            cursor="pointer"
                            onClick={() => removeSlackUser(user.id!)}
                            _hover={{ bg: "purple.200" }}
                            textTransform="none"
                          >
                            {user.display_name || user.name} ✕
                          </Badge>
                        ))}
                      </Flex>
                    )}
                  </FormControl>
                </FormControl>
              </VStack>
            </Collapse>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1}/>

        {/* Scenarios Section */}
        <VStack spacing={3} align="stretch">
          <FormControl>
            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleScenariosExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
            >
              <Icon as={FaListUl} boxSize={5} color="fg.default" />
              <FormLabel fontSize="md" mb={0} cursor="pointer" fontWeight="700" color="fg.default">
                Scenarios {scenarios.length > 0 && (
                  <Badge colorScheme="purple" ml={2}>{scenarios.length}</Badge>
                )}
              </FormLabel>
              {!isScenariosExpanded && (
                <Text fontSize="sm" color="gray.600" mb={0} align="left" ml={1} mt={1}>
                  Define specific scenarios and investigation steps for the agent
                </Text>
              )}
            </HStack>

            <Collapse in={isScenariosExpanded} animateOpacity>
              <VStack spacing={3} align="stretch" p={3}>
                <HStack justify="space-between" align="center">
                  <Button
                    size="sm"
                    leftIcon={<Icon as={FiPlus} />}
                    colorScheme="purple"
                    variant="solid"
                    onClick={addScenario}
                    minW="auto"
                    flexShrink={0}
                    bg="purple.700"
                    fontWeight="700"
                  >
                    Add Scenario
                  </Button>
                </HStack>

                {scenarios?.map((scenario, index) => {
                  const isExpanded = expandedScenarioIndex === index;
                  return (
                  <Box
                    key={scenario.id || index}
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="gray.50"
                  >
                      <HStack
                        justify="space-between"
                        align="center"
                        p={2}
                        cursor="pointer"
                        onClick={() => toggleScenarioExpansion(index)}
                        _hover={{ bg: "gray.100" }}
                        borderRadius="md"
                      >
                        <HStack spacing={2}>
                          <Text fontSize="sm" fontWeight="600" color="fg.default" mb={0}>
                        Scenario {index + 1}
                      </Text>
                          {scenario?.issue && !isExpanded && (
                            <Text fontSize="xs" color="gray.600" isTruncated maxW="200px" mb={0}>
                              - {scenario?.issue}
                            </Text>
                          )}
                        </HStack>
                        <HStack spacing={1}>
                          <HStack spacing={1} align="center" mr={2}>
                            <Text fontSize="xs" color="gray.600" fontWeight="semibold" mb={0}>
                              {!(scenario.disable ?? false) ? "Enabled" : "Disabled"}
                            </Text>
                            <Switch
                              size="sm"
                              colorScheme="purple"
                              isChecked={!(scenario.disable ?? false)}
                              mb={0}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateScenario(index, "disable", !e.target.checked);
                              }}
                            />
                          </HStack>
                          <Tooltip label="Delete Scenario">
                            <IconButton
                              aria-label="Delete scenario"
                              icon={<DeleteIcon />}
                              size="sm"
                              variant="ghost"
                        colorScheme="red"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeScenario(index);
                              }}
                            />
                          </Tooltip>
                          <IconButton
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                            icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            size="sm"
                        variant="ghost"
                            onClick={() => toggleScenarioExpansion(index)}
                          />
                        </HStack>
                    </HStack>

                      <Collapse in={isExpanded} animateOpacity>
                        <VStack spacing={3} align="stretch" p={2} pt={0}>
                      <FormControl>
                        <FormLabel fontSize="xs" mb={1} fontWeight="600">Issue Description</FormLabel>
                        <Textarea
                          size="sm"
                          value={scenario.issue || ""}
                          onChange={(e) => updateScenario(index, "issue", e.target.value)}
                          placeholder="Describe the issue or scenario..."
                          rows={3}
                          _focus={{
                            borderColor: "purple.700",
                            boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                          }}
                          fontWeight="500"
                          resize="none"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="xs" mb={1} fontWeight="600">Trigger Conditions</FormLabel>
                        <Textarea
                          size="sm"
                          value={scenario.trigger || ""}
                          onChange={(e) => updateScenario(index, "trigger", e.target.value)}
                          placeholder="What conditions activate this scenario..."
                          rows={2}
                          _focus={{
                            borderColor: "purple.700",
                            boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                          }}
                          fontWeight="500"
                          resize="none"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="xs" mb={1} fontWeight="600">Investigation Steps</FormLabel>
                        <Textarea
                          size="sm"
                          value={scenario.investigation || ""}
                          onChange={(e) => updateScenario(index, "investigation", e.target.value)}
                          placeholder="Steps to diagnose the root cause..."
                          rows={3}
                          _focus={{
                            borderColor: "purple.700",
                            boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                          }}
                          fontWeight="500"
                          resize="none"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="xs" mb={1} fontWeight="600">Reporting Instructions</FormLabel>
                        <Textarea
                          size="sm"
                          value={scenario.reporting || ""}
                          onChange={(e) => updateScenario(index, "reporting", e.target.value)}
                          placeholder="How to report findings for this scenario..."
                          rows={3}
                          _focus={{
                            borderColor: "purple.700",
                            boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                          }}
                          fontWeight="500"
                          resize="none"
                        />
                      </FormControl>
                    </VStack>
                      </Collapse>
                  </Box>
                  );
                })}

                {scenarios.length === 0 && (
                  <Box p={4} textAlign="center">
                    <Text fontSize="sm" color="gray.500">
                      No scenarios added yet. Click "Add Scenario" to create your first scenario.
                    </Text>
                  </Box>
                )}
              </VStack>
            </Collapse>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1}/>

        {/* Model Configuration Section */}
        <VStack spacing={3} align="stretch">
          <FormControl>
            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleModelConfigExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
            >
              <Icon as={FiSettings} boxSize={5} color="fg.default" />
              <FormLabel fontSize="md" mb={0} cursor="pointer" fontWeight="700" color="fg.default">
                Model Configuration
              </FormLabel>
              {!isModelConfigExpanded && (
                <Text fontSize="sm" color="gray.600" mb={0} align="left" ml={1} mt={1}>
                  Configure the LLM model settings for the agent
                </Text>
              )}
            </HStack>

            <Collapse in={isModelConfigExpanded} animateOpacity>
              <VStack spacing={3} align="stretch" p={3}>
                <HStack spacing={3} align="end">
                  <FormControl maxW="300px">
                    <FormLabel fontSize="sm" mb={1} fontWeight="600">Model Name</FormLabel>
                    <Select
                      size="sm"
                      value={modelConfig.model_name || ""}
                      onChange={(e) => handleModelNameChange(e.target.value)}
                      placeholder="Select a model"
                      _focus={{
                        borderColor: "purple.700",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                      }}
                      fontWeight="500"
                      borderRadius={8}
                    >
                      {Object.entries(modelOptions).map(([provider, models]) => (
                        <optgroup key={provider} label={provider.replace("_", " ").toUpperCase()}>
                          {models.map(({model_name, model_options}) => (
                            <option key={model_name} value={model_name}>
                              {model_name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl maxW="120px" isInvalid={!!temperatureError}>
                    <FormLabel fontSize="sm" mb={1} fontWeight="600">Temperature</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      step="0.1"
                      value={modelConfig.temperature?.toString() || ""}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        validateTemperature(inputValue);

                        if (inputValue === "") {
                          updateModelConfig("temperature", 0.0);
                        } else {
                          const numValue = parseFloat(inputValue);
                          if (!isNaN(numValue)) {
                            updateModelConfig("temperature", numValue);
                          }
                        }
                      }}
                      placeholder="0.7"
                      _focus={{
                        borderColor: "purple.700",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                      }}
                      fontWeight="500"
                    />
                  </FormControl>
                  {temperatureError && (
                    <Text fontSize="xs" color="red.500" mb={2}>
                      {temperatureError}
                    </Text>
                    )}
                </HStack>
              </VStack>
            </Collapse>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1}/>

        {/* Schedule Configuration */}
        <VStack spacing={3} align="stretch">
          <FormControl>
            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleScheduleExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
            >
              <Icon as={FiClock} boxSize={5} color="fg.default" />
              <Text fontSize="md" mb={0} cursor="pointer" fontWeight="700" color="fg.default">
                Schedule
              </Text>
              {!isScheduleExpanded && (
                <Text fontSize="sm" color="gray.600" mb={0} align="left" ml={1} mt={1}>
                  Define when the agent automatically runs analysis and sends updates
                </Text>
              )}
            </HStack>
            <Collapse in={isScheduleExpanded} animateOpacity>
              <Box border="1px" borderColor="gray.200" borderRadius="md" p={0}>
                {hasValidInitialData && (
                  <ScheduledJob jobHeader={initialData?.header} />
                )}
                {!hasValidInitialData && (
                  <Box p={4}>
                    <Text fontSize="sm" color="gray.500">
                      Save the agent first to configure scheduling
                    </Text>
                  </Box>
                )}
              </Box>
            </Collapse>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1}/>

        {/* Form Actions */}
        {isCreatingNew && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          bg="white"
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="lg"
          p={4}
          zIndex={1000}
          minW="300px"
          maxW="400px"
         >
           <HStack spacing={3} justify="space-around">

            <Button
              colorScheme="green"
              size="md"
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText={hasValidInitialData ? "Updating..." : "Creating..."}
              leftIcon={<Icon as={FaCheck} boxSize={3} />}
            >
              {hasValidInitialData ? "Update" : "Create Agent"}
            </Button>
            <Button
              colorScheme="red"
              variant="outline"
              size="md"
              onClick={onCancel}
              isDisabled={isLoading}
              leftIcon={<Icon as={FaTimes} boxSize={3}/>}
            >
              Cancel
            </Button>
          </HStack>
        </Box>
        )}
      </Box>
    </Box>
  );
};

export default AgentForm;